import { useEffect, useState } from "react";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { useDebounce } from "../../hooks/useDebounce";
import { Spinner } from "../Spinner";
import { FieldShell } from "./FieldShell";
import { fieldIds, inputClass, resolveFieldState } from "./fieldStyles";

export interface SearchableOption {
  value: string;
  label: string;
  description?: string;
}

interface SearchableSelectInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string, option: SearchableOption | null) => void;
  /** Async option source; called with the debounced query. */
  loadOptions: (query: string, signal: AbortSignal) => Promise<readonly SearchableOption[]>;
  /** Label to show for the current value before options load (e.g. copy mode). */
  selectedLabel?: string;
  error?: string | null;
  hint?: string;
  required?: boolean;
  placeholder?: string;
  containerClassName?: string;
  children?: never;
}

/** Async typeahead built on Headless UI Combobox. Backs the Opportunity picker. */
export function SearchableSelectInput({ id, label, value, onChange, loadOptions, selectedLabel, error, hint, required, placeholder = "Search...", containerClassName }: SearchableSelectInputProps) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<readonly SearchableOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debouncedQuery = useDebounce(query, 300);
  const ids = fieldIds(id, error, hint);
  const state = resolveFieldState({ error });
  const selected = options.find((option) => option.value === value) ?? (value ? { value, label: selectedLabel ?? value } : null);

  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      setIsLoading(true);
      try {
        const loaded = await loadOptions(debouncedQuery, controller.signal);
        if (!controller.signal.aborted) setOptions(loaded);
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };
    void run();
    return () => controller.abort();
  }, [debouncedQuery, loadOptions]);

  const handleChange = (next: string | null) => {
    const option = options.find((candidate) => candidate.value === next) ?? null;
    onChange(next ?? "", option);
  };

  return (
    <FieldShell id={id} label={label} required={required} hint={hint} hintId={ids.hintId} error={error} errorId={ids.errorId} containerClassName={containerClassName}>
      <Combobox value={value} onChange={handleChange} onClose={() => setQuery("")} immediate>
        <div className="relative">
          <ComboboxInput
            id={id}
            autoComplete="off"
            displayValue={() => selected?.label ?? ""}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={placeholder}
            aria-required={required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={ids.describedBy}
            className={`${inputClass(state)} pr-10`}
          />
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">{isLoading ? <Spinner size="sm" /> : <ChevronDown size={16} aria-hidden="true" />}</span>
          <ComboboxOptions anchor="bottom start" className="z-50 max-h-64 w-[var(--input-width)] overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none [--anchor-gap:4px]">
            {options.length === 0 && !isLoading && <div className="px-4 py-2.5 text-gray-500">No matches.</div>}
            {options.map((option) => (
              <ComboboxOption key={option.value} value={option.value} className="relative cursor-pointer select-none py-2.5 pl-10 pr-4 text-gray-900 data-[focus]:bg-brand-green-10 data-[focus]:text-brand-green">
                {({ selected: isSelected }) => (
                  <>
                    {isSelected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-green">
                        <Check size={16} aria-hidden="true" />
                      </span>
                    )}
                    <span className="block font-medium">{option.label}</span>
                    {option.description && <span className="block text-xs text-gray-500">{option.description}</span>}
                  </>
                )}
              </ComboboxOption>
            ))}
          </ComboboxOptions>
        </div>
      </Combobox>
    </FieldShell>
  );
}
